import { CalendarDays, ChartColumn, Factory, HardHat, Landmark, MessagesSquare, type LucideIcon } from "lucide-react";

/**
 * Areas from the discovery functional map that are not built yet. They appear in the navigation so
 * people can see where K-SHOP is going, and every page renders the same "Under construction" page
 * (pages/tenant/UnderConstructionPage.tsx). No data, API or permission sits behind them.
 *
 * When an area is built, remove its entry here and give it a real section in navigation.ts.
 * Paths are relative to /t/<slug>/; a section's key is its first path segment, and its first page
 * is its landing page (the same convention as the built sections).
 */
/** `permission`: for a page inside a built area, the capability that area's routes require. */
export type PlannedPage = { path: string; label: string; summary: string; permission?: string };
export type PlannedSection = { key: string; label: string; icon: LucideIcon; pages: PlannedPage[] };

export const plannedSections: PlannedSection[] = [
  {
    // Built: Shop floor, Daily report and Stages (navigation.ts "production", ADR-034); the rest is planned.
    key: "production",
    label: "Production",
    icon: Factory,
    pages: [
      { path: "production/processing", label: "Processing", summary: "Everything the shop needs (thickness, colour, edges, sinks, seams, overhangs and cut-outs), checked complete before a job is released." },
      { path: "production/sinks", label: "Sink library", summary: "Sink and faucet-hole templates with their verification status." },
      { path: "production/quality", label: "Quality & re-dos", summary: "Quality checks, and re-dos recorded with their cause and cost." },
    ],
  },
  {
    // Built: Calendar, Look-ahead, History, Crews & capacity and Work settings (navigation.ts
    // "schedule"). The entry stays for the area's place, name and icon; it has no planned pages.
    key: "schedule",
    label: "Schedule",
    icon: CalendarDays,
    pages: [],
  },
  {
    key: "field",
    label: "Field",
    icon: HardHat,
    pages: [
      { path: "field/work-orders", label: "Installer work orders", summary: "Work orders at installer rates, with material specifications and responsibilities." },
      // "Completion & punch lists" is built: My work (field/work, navigation.ts "field").
      { path: "field/installer-pay", label: "Installer pay", summary: "Weekly installer pay from completed work orders." },
    ],
  },
  {
    key: "accounting",
    label: "Accounting",
    icon: Landmark,
    pages: [
      // Invoices, payments, receivables and the books (ledger, bills, bank, statements, close) are built
      // (navigation.ts, "accounting"); sales tax returns are still planned.
      { path: "accounting/sales-tax", label: "Sales tax returns", summary: "What was collected per jurisdiction, ready for the returns." },
    ],
  },
  {
    key: "messages",
    label: "Messages",
    icon: MessagesSquare,
    pages: [
      { path: "messages", label: "Inbox", summary: "Questions and mentions kept with the job, instead of spread across email and phone calls." },
      { path: "messages/customer-updates", label: "Customer updates", summary: "Customer notifications sent on rules you configure, with message and call history." },
    ],
  },
  {
    key: "reports",
    label: "Reports",
    icon: ChartColumn,
    pages: [
      // The operational reports are built (navigation.ts "reports", reports.view_operations); these are still planned.
      { path: "reports/attention", label: "What needs attention", summary: "The exceptions that need you today, for your role." },
      { path: "reports/management-pack", label: "Management pack", summary: "The monthly management report built from K-SHOP data, with budget and history." },
      { path: "reports/exports", label: "Exports", summary: "Excel and CSV exports of the data you can see." },
    ],
  },
];

/** Planned pages inside sections that already exist (shown only to people who can see that section). */
export const plannedSalesPages: PlannedPage[] = [
  { path: "sales", label: "Enquiries", summary: "Walk-ins, referrals and builder requests with their follow-ups and site intake questions, kept together." },
];
export const plannedJobsPages: PlannedPage[] = [
  { path: "jobs/costing", label: "Job costing", summary: "The margin a job was sold at, and how it moves with purchase orders, stock pulled, work orders and re-dos." },
];
export const plannedOrderingPages: PlannedPage[] = [
  { path: "ordering/slabs", label: "Slabs & remnants", summary: "Slab and remnant inventory, and which job each one is allocated to." },
];
export const plannedTeamPages: PlannedPage[] = [
  { path: "team/employees", permission: "team.view_membership", label: "Employees", summary: "Employee records with their certifications, and alerts before they expire." },
];
export const plannedSettingsPages: PlannedPage[] = [
  { path: "settings/divisions", permission: "tenant.manage_settings", label: "Divisions", summary: "Divisions such as flooring and countertops, for numbering, printing and reports." },
  { path: "settings/numbering", permission: "tenant.manage_settings", label: "Numbering & templates", summary: "Document numbering, statuses and templates." },
];

const builtSectionLabels: Record<string, string> = { ordering: "Ordering", team: "Team", settings: "Company settings", sales: "Sales", jobs: "Jobs", field: "Field" };

export const plannedPages: Array<PlannedPage & { section: string }> = [
  ...plannedSections.flatMap((s) => s.pages.map((p) => ({ ...p, section: s.label }))),
  ...[...plannedSalesPages, ...plannedJobsPages, ...plannedOrderingPages, ...plannedTeamPages, ...plannedSettingsPages].map((p) => ({ ...p, section: builtSectionLabels[p.path.split("/")[0]] })),
];

/** The planned page for a tenant path such as /t/acme/sales/estimates (trailing slash tolerated). */
export function plannedPageFor(pathname: string) {
  const relative = pathname.replace(/^\/t\/[^/]+\/?/, "").replace(/\/+$/, "");
  return plannedPages.find((p) => p.path === relative) ?? null;
}
